import React, { createContext, useContext, useEffect, useMemo } from "react";
import { QueryClientProvider, defaultShouldDehydrateQuery, useQueryClient } from "@tanstack/react-query";
import { persistQueryClient } from "@tanstack/react-query-persist-client";
import { getQueryClient } from "./queryClient";
import type { SessionUser } from "../types/auth";
import type { UserStateMap } from "../types/user-state";
import { useUserQuery } from "./user/useUserQuery";
import { useUserStateQuery } from "./user-state/useUserStateQuery";
import { userStateQueryKey } from "./user-state/userStateQueryKeys";
import { createUserStatePersister } from "./user-state/userStatePersister";
import { isPersistedUserStateQuery } from "./user-state/userStatePersistenceScope";

export interface UserContextValue {
  user: SessionUser | null;
  hasBackofficeAccess: boolean;
  isLoadingUser: boolean;
  isAuthenticated: boolean;
  userState: UserStateMap | undefined;
  isLoadingUserState: boolean;
  isFetchingUserState: boolean;
  userStateError: unknown;
  /**
   * Resolves once this `UserProvider`'s restore-and-hydrate cycle has genuinely finished — not
   * merely once the persister's own IndexedDB read finished. Those are different moments, and the
   * gap between them is exactly where a real, twice-reproduced live data-loss bug lived: a
   * mutation's `onMutate` writing into `userStateQueryKey` in that gap silently and permanently
   * discards the restored data (`hydrate()` only applies a restored query when its `dehydratedAt`
   * is newer than the query's current `dataUpdatedAt` — an optimistic write's `dataUpdatedAt` is
   * "now," always newer than a snapshot from an earlier page load).
   *
   * Two prior approaches were tried and both had real, live-reproduced timing bugs, kept here as
   * a record of what NOT to do:
   * 1. A persister-level promise resolved inside `restoreClient()`'s own completion. Wrong: that
   *    fires *before* the caller (`PersistQueryClientProvider`) has actually called `hydrate()`
   *    with the data — `restoreClient()`'s caller only calls `hydrate()` in a microtask *after*
   *    its own `await` of `restoreClient()` settles, so this signal arrived one microtask early.
   * 2. `useIsRestoring()` (the library's own hook for this). Wrong in practice: it starts `false`
   *    and only flips `true` once `PersistQueryClientProvider`'s internal effect actually begins
   *    restoring — but React runs child effects before parent effects, so a child's mount-time
   *    write (e.g. `recordViewedJob` firing on job-detail mount) can run *before* that parent
   *    effect ever flips the flag, reading a stale `false` and never waiting at all.
   *
   * The actual fix: `UserProvider` does NOT use `PersistQueryClientProvider`'s automatic,
   * effect-timed restore at all. It calls the lower-level `persistQueryClient()` directly, during
   * render (not in an effect — see `UserProviderInner`), and this promise is that call's own
   * returned restore promise, which only resolves after `hydrate()` has genuinely run, and starts
   * as early as it's possible to start (render, not commit/effects) so no child effect can outrun
   * it.
   */
  waitForUserStateRestored: () => Promise<void>;
}

const UserContext = createContext<UserContextValue | undefined>(undefined);

export interface UserProviderProps {
  children: React.ReactNode;
  /**
   * SSR-provided session (e.g. `pageContext.user`, the same value `AuthProvider`'s `initialUser`
   * prop receives) — seeds the identity query so first paint doesn't show a loading state. Only
   * a truthy value skips the client fetch, mirroring `AuthContext.tsx`'s own `isLoading` check.
   */
  initialUser?: SessionUser | null;
  initialHasBackofficeAccess?: boolean;
  /**
   * SSR-provided user-state map (e.g. `pageContext.userState` — see `redux/createStore.ts`'s
   * `applySsrStateHydration` for the shape this replicates) — seeds the user-state query cache
   * so a page's server-rendered saved/viewed/applied state etc. hydrates instantly instead of
   * showing a loading state until the client refetches it.
   */
  initialUserState?: UserStateMap;
}

/** Module-level so this object's identity is stable across renders — not load-bearing for correctness, just avoids handing `persistQueryClient` a new options object every render. */
const userStateDehydrateOptions = {
  shouldDehydrateQuery: (query: { queryKey: readonly unknown[] }) =>
    defaultShouldDehydrateQuery(query as never) && isPersistedUserStateQuery(query.queryKey),
};

/**
 * Module-level (keyed by `QueryClient` identity, not by `UserProviderInner` mount) so the
 * restore-and-subscribe cycle below runs exactly once per `QueryClient` — see the comment on
 * `getUserStateRestore` for why a per-mount version of this was a real, live-reproduced bug.
 */
let userStateRestoreSingleton: { queryClient: QueryClient; promise: Promise<void> } | null = null;

/**
 * Starts (once) or reuses the `persistQueryClient()` restore-and-subscribe cycle for `queryClient`.
 *
 * This used to be a `useRef` inside `UserProviderInner`, so every mount of that component started
 * its own restore-and-subscribe cycle — including on remount. `pages/+onRenderClient.ts`'s
 * `comingFromSrp`/`comingFromJob`/etc. branches force a full `createRoot()` remount (old root
 * `.unmount()`ed, brand-new tree rendered) whenever navigating away from an island page, e.g. SRP
 * -> `/tools` right after the SRP island's "view saved jobs" toast. `queryClient` itself is the
 * module-level singleton from `queryClient.ts` and survives that remount untouched, but a second
 * `UserProviderInner` mount used to still call `persistQueryClient()` again on it — starting a
 * second, redundant `restoreClient()` read of the same IndexedDB snapshot and a second dehydrate
 * subscription, while the *first* subscription had just been torn down by the outgoing instance's
 * unmount cleanup. That's a second, unnecessary restore-and-hydrate cycle racing whatever the first
 * cycle's mutations already wrote — exactly the class of bug this file's other doc comments already
 * describe two prior, live-reproduced variants of. Scoping this to the `QueryClient` instance
 * instead removes the second cycle entirely: one `QueryClient` (one page session, since it's a
 * client-side singleton) gets exactly one restore and one persist subscription, no matter how many
 * times `UserProviderInner` itself remounts.
 */
function getUserStateRestore(queryClient: QueryClient): Promise<void> {
  if (userStateRestoreSingleton?.queryClient === queryClient) {
    return userStateRestoreSingleton.promise;
  }
  const persister = createUserStatePersister();
  const [, promise] = persistQueryClient({
    queryClient,
    persister,
    dehydrateOptions: userStateDehydrateOptions,
  });
  userStateRestoreSingleton = { queryClient, promise };
  return promise;
}

function UserProviderInner({
  children,
  initialUser,
  initialHasBackofficeAccess,
  initialUserState,
}: UserProviderProps) {
  const queryClient = useQueryClient();

  // Kicks off (or reuses) restore during RENDER, not in a useEffect — see
  // UserContextValue.waitForUserStateRestored's doc comment for exactly why effect-timed
  // restoration (both a prior persister-level promise and useIsRestoring()) had real,
  // live-reproduced races with child effects (e.g. recordViewedJob firing on job-detail mount)
  // that could run before an effect-based restore signal ever fired. Starting this during render
  // means nothing downstream can ever run before it starts; scoping it to `queryClient` (see
  // getUserStateRestore) rather than to this component's own mount means remounting
  // `UserProviderInner` (e.g. the forced createRoot() in `pages/+onRenderClient.ts` when
  // navigating away from an island page) reuses the same in-flight/completed restore instead of
  // starting a second, redundant one.
  // The call has to actually happen here, not only inside waitForUserStateRestored: a guest has no
  // user-state fetch to trigger it, so a read-only page (e.g. a hard load of /tools) never restored
  // the persisted cache at all and showed an empty My Jobs board until something wrote.
  if (typeof window !== 'undefined') void getUserStateRestore(queryClient);
  const waitForUserStateRestored = useMemo(() => () => getUserStateRestore(queryClient), [queryClient]);

  const userQuery = useUserQuery(
    initialUser
      ? { initialData: { user: initialUser, hasBackofficeAccess: initialHasBackofficeAccess ?? false } }
      : undefined
  );

  const user = userQuery.data?.user ?? null;

  // Only a signed-in visitor's SSR snapshot is the real thing (server/vike-handler.ts's
  // listUserState). A guest's state lives only in this browser, so any guest initialUserState is a
  // partial stand-in (e.g. SrpApp.tsx's saved-ids-only badge seed). It may paint first, but must
  // lose to the IndexedDB restore (initialDataUpdatedAt 0) and must never overwrite the cache
  // below; otherwise the stand-in gets persisted and wipes the guest's viewed/applied entries.
  const initialUserStateIsAuthoritative = Boolean(initialUser);

  const userStateQuery = useUserStateQuery({
    enabled: Boolean(user),
    initialData:
      initialUserState && Object.keys(initialUserState).length > 0 ? initialUserState : undefined,
    initialDataUpdatedAt: initialUserStateIsAuthoritative ? undefined : 0,
    waitForRestored: waitForUserStateRestored,
  });

  // `initialData` above only seeds the cache the first time this queryClient sees
  // `userStateQueryKey` — it's silently ignored on every later render/mount once a cache entry
  // already exists, per TanStack Query's own semantics. `userStateQueryKey` is one shared,
  // page-independent key, so on a client-side (SPA) navigation to a new page, that page's own
  // freshly-fetched, correct `initialUserState` (server/vike-handler.ts's `listUserState`, run
  // fresh on every navigation) was being thrown away in favor of whatever this tab happened to
  // cache first — which, combined with the restore/fetch race documented on
  // `waitForUserStateRestored` above, could be an empty or incomplete snapshot that then sat
  // "fresh" (queryClient.ts's deliberate 30s staleTime) and kept getting served with no refetch
  // and no loading state at all, live-reproduced as: jobs missing after an in-app navigation to
  // /tools, fixed only by a hard reload (which resets this client-side cache entirely and forces
  // a genuinely fresh server round-trip). Explicitly (re)writing a real, non-empty
  // `initialUserState` into the cache on every navigation makes the server's answer authoritative
  // again, matching what a hard reload already did correctly. Skipped when empty: an empty
  // `initialUserState` can mean "genuinely zero saved/applied jobs" OR "this page never fetched
  // it at all" (e.g. a static-owned path, see vike-handler.ts's `isStaticOwnedPath` branch) —
  // those are indistinguishable here, so, same as the `initialData` line above, only a confirmed
  // non-empty snapshot is trusted enough to overwrite the cache; the query's own fetch handles
  // the rest. `setQueryData`'s default structural sharing means this is a no-op re-render when the
  // incoming snapshot is unchanged from what's already cached.
  useEffect(() => {
    if (initialUserStateIsAuthoritative && initialUserState && Object.keys(initialUserState).length > 0) {
      queryClient.setQueryData(userStateQueryKey, initialUserState);
    }
  }, [queryClient, initialUserState, initialUserStateIsAuthoritative]);

  const value = useMemo<UserContextValue>(
    () => ({
      user,
      hasBackofficeAccess: userQuery.data?.hasBackofficeAccess ?? false,
      isLoadingUser: userQuery.isLoading,
      isAuthenticated: Boolean(user),
      userState: userStateQuery.data,
      isLoadingUserState: userStateQuery.isLoading,
      isFetchingUserState: userStateQuery.isFetching,
      userStateError: userStateQuery.error,
      waitForUserStateRestored,
    }),
    [
      user,
      userQuery.data?.hasBackofficeAccess,
      userQuery.isLoading,
      userStateQuery.data,
      userStateQuery.isLoading,
      userStateQuery.isFetching,
      userStateQuery.error,
      waitForUserStateRestored,
    ]
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

/**
 * TEC-404: additive TanStack Query + Context replacement layer for user identity and user-state.
 * Mounted in the main app tree and both SRP/job-detail islands; `contexts/AuthContext.tsx`
 * (identity) and the Redux `userStateApi` (user-state) remain the live paths powering every
 * *existing* call site — this is migrated to incrementally, call site by call site, as separate
 * follow-up work.
 *
 * Wraps the shared `QueryClient` singleton from `query/queryClient.ts` in a plain
 * `QueryClientProvider` so that once this *is* mounted in the SRP island, job-detail island, and
 * main app tree, they share one cache — the same reasoning behind `redux/createStore.ts`'s
 * `clientStoreSingleton`: giving each island its own throwaway store/client means a mutation from
 * one island (e.g. saving a job from the job page) would never be visible to the other (e.g. the
 * SRP's saved-jobs list).
 *
 * Deliberately NOT `PersistQueryClientProvider` — restoration is instead driven manually by
 * `UserProviderInner` calling `persistQueryClient()` directly during render, not in an effect. See
 * `UserContextValue.waitForUserStateRestored`'s doc comment for why: `PersistQueryClientProvider`
 * restores on an internal effect, which a child's own mount-time effect (e.g. `recordViewedJob`
 * firing on job-detail mount) can outrun.
 *
 * The persister (`userStatePersister.ts`) is what gives anonymous users the same local-first
 * persistence Redux's `redux-persist` + `localforage` already gives them for `userState` —
 * without it, a guest's saved/viewed/applied jobs would vanish on reload, since
 * `useUserStateQuery` only fetches from the server once signed in (`enabled: !!user`). Scoped via
 * `dehydrateOptions.shouldDehydrateQuery` (see `userStateDehydrateOptions` in `UserProviderInner`)
 * to persist *only* the `userStateQueryKey` query (`isPersistedUserStateQuery`) — identity
 * (`useUserQuery`'s `userQueryKey`) is deliberately excluded and always re-resolves from the
 * session cookie on every page load; see `userStatePersistenceScope.ts` for why.
 */
export function UserProvider(props: UserProviderProps) {
  const queryClient = useMemo(() => getQueryClient(), []);
  return (
    <QueryClientProvider client={queryClient}>
      <UserProviderInner {...props} />
    </QueryClientProvider>
  );
}

export interface UseUserResult {
  user: SessionUser | null;
  hasBackofficeAccess: boolean;
  isLoading: boolean;
  isAuthenticated: boolean;
}

/** Identity — the eventual replacement for `useAuthContext()`. Must be called within `UserProvider`. */
export function useUser(): UseUserResult {
  const ctx = useContext(UserContext);
  if (ctx === undefined) {
    throw new Error("useUser must be used within UserProvider");
  }
  return {
    user: ctx.user,
    hasBackofficeAccess: ctx.hasBackofficeAccess,
    isLoading: ctx.isLoadingUser,
    isAuthenticated: ctx.isAuthenticated,
  };
}

export interface UseUserStateResult {
  data: UserStateMap | undefined;
  isLoading: boolean;
  isFetching: boolean;
  error: unknown;
}

/**
 * The full user-state map ("favourites, etc." per the ticket, scoped to what's actually there —
 * saved/viewed/applied jobs, profile, preferences, saved searches, and the rest of
 * `UserStateItemKey`). Chained off `useUser()` internally (only fetches once signed in). Must be
 * called within `UserProvider`.
 */
export function useUserState(): UseUserStateResult {
  const ctx = useContext(UserContext);
  if (ctx === undefined) {
    throw new Error("useUserState must be used within UserProvider");
  }
  return {
    data: ctx.userState,
    isLoading: ctx.isLoadingUserState,
    isFetching: ctx.isFetchingUserState,
    error: ctx.userStateError,
  };
}

/**
 * Returns a function mutations must `await` before their first read/write of the
 * `userStateQueryKey` cache — see `UserContextValue.waitForUserStateRestored`'s doc comment for
 * the exact data-loss race this closes. Must be called within `UserProvider`.
 */
export function useWaitForUserStateRestored(): () => Promise<void> {
  const ctx = useContext(UserContext);
  if (ctx === undefined) {
    throw new Error("useWaitForUserStateRestored must be used within UserProvider");
  }
  return ctx.waitForUserStateRestored;
}
